<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'

interface Session {
  authenticated: boolean
  csrfToken: string
}

interface BotState {
  slug: string
  title: string
  enabled: boolean
  status: 'disabled' | 'starting' | 'running' | 'degraded' | 'failed' | 'stopped'
  username: string | null
  failures: number
  lastError: string | null
  lastUpdateAt: string | null
}

interface FunnelRow {
  botSlug: string
  starts: number
  freeResults: number
  payments: number
  renewals: number
}

interface MoneyRow {
  botSlug: string
  revenueKopecks: number
  paymentCount: number
}

interface UsageRow {
  botSlug: string
  eventCount: number
  durationMs: number
  volumeUnits: number
  costKopecks: number
  users: number
  costPerUserKopecks: number
}

interface ReferralRow {
  botSlug: string
  referralCode: string
  invited: number
  paidUsers: number
  revenueKopecks: number
}

interface Dashboard {
  period: { from: string; to: string }
  funnel: FunnelRow[]
  money: MoneyRow[]
  usage: UsageRow[]
  referrals: ReferralRow[]
}

interface FailedJob {
  id: string
  botSlug: string
  operationKind: string
  attempts: number
  maxAttempts: number
  error: string | null
  updatedAt: string
}

interface Payment {
  paymentId: string | null
  orderId: string
  botSlug: string
  telegramUserId: string
  amountKopecks: number
  status: string
  receiptUrl: string | null
  createdAt: string
}

const session = ref<Session | null>(null)
const bots = ref<BotState[]>([])
const dashboard = ref<Dashboard | null>(null)
const jobs = ref<FailedJob[]>([])
const payments = ref<Payment[]>([])
const code = ref('')
const codeRequested = ref(false)
const loading = ref(true)
const actionLoading = ref(false)
const error = ref('')

function dateInput(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const today = new Date()
const thirtyDaysAgo = new Date(today.getTime() - 29 * 24 * 60 * 60_000)
const from = ref(dateInput(thirtyDaysAgo))
const to = ref(dateInput(today))
const selectedBot = ref('')

async function api<T>(url: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  if (init.body && !headers.has('content-type')) headers.set('content-type', 'application/json')
  if (session.value?.csrfToken && init.method && init.method !== 'GET') {
    headers.set('x-csrf-token', session.value.csrfToken)
  }
  const response = await fetch(url, { ...init, headers, credentials: 'same-origin' })
  const payload = await response.json().catch(() => ({})) as Record<string, unknown>
  if (!response.ok) {
    throw new Error(typeof payload.message === 'string' ? payload.message : 'Запрос не выполнен.')
  }
  return payload as T
}

function exclusiveTo(value: string): string {
  const result = new Date(`${value}T00:00:00`)
  result.setDate(result.getDate() + 1)
  return result.toISOString()
}

async function loadDashboard(): Promise<void> {
  loading.value = true
  error.value = ''
  try {
    const query = new URLSearchParams({
      from: new Date(`${from.value}T00:00:00`).toISOString(),
      to: exclusiveTo(to.value),
    })
    if (selectedBot.value) query.set('bot', selectedBot.value)
    const [dashboardResult, jobResult, paymentResult, botResult] = await Promise.all([
      api<Dashboard>(`/api/admin/dashboard?${query}`),
      api<{ jobs: FailedJob[] }>('/api/admin/jobs/failed?limit=50'),
      api<{ payments: Payment[] }>('/api/admin/payments?limit=50'),
      api<{ bots: BotState[] }>('/api/admin/bots'),
    ])
    dashboard.value = dashboardResult
    jobs.value = jobResult.jobs
    payments.value = paymentResult.payments
    bots.value = botResult.bots
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : 'Не удалось загрузить данные.'
  } finally {
    loading.value = false
  }
}

async function requestCode(): Promise<void> {
  actionLoading.value = true
  error.value = ''
  try {
    await api('/api/admin/auth/request-code', { method: 'POST', body: '{}' })
    codeRequested.value = true
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : 'Не удалось отправить код.'
  } finally {
    actionLoading.value = false
  }
}

async function login(): Promise<void> {
  actionLoading.value = true
  error.value = ''
  try {
    const result = await api<Session>('/api/admin/auth/login', {
      method: 'POST',
      body: JSON.stringify({ code: code.value }),
    })
    session.value = result
    code.value = ''
    await loadDashboard()
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : 'Не удалось войти.'
  } finally {
    actionLoading.value = false
  }
}

async function logout(): Promise<void> {
  const result = await api<Session>('/api/admin/auth/logout', {
    method: 'POST',
    body: '{}',
  })
  session.value = result
  dashboard.value = null
  codeRequested.value = false
}

const revenueKopecks = computed(
  () => dashboard.value?.money.reduce((sum, row) => sum + row.revenueKopecks, 0) ?? 0,
)
const costKopecks = computed(
  () => dashboard.value?.usage.reduce((sum, row) => sum + row.costKopecks, 0) ?? 0,
)
const paymentCount = computed(
  () => dashboard.value?.money.reduce((sum, row) => sum + row.paymentCount, 0) ?? 0,
)
const allBotSlugs = computed(() => [...new Set([
  ...bots.value.map(bot => bot.slug),
  ...(dashboard.value?.funnel.map(row => row.botSlug) ?? []),
])])

function rubles(kopecks: number): string {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 2,
  }).format(kopecks / 100)
}

function integer(value: number): string {
  return new Intl.NumberFormat('ru-RU').format(value)
}

function when(value: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value))
}

function botTitle(slug: string): string {
  return bots.value.find(bot => bot.slug === slug)?.title ?? slug
}

function statusTitle(status: BotState['status']): string {
  return {
    disabled: 'выключен',
    starting: 'запускается',
    running: 'работает',
    degraded: 'переподключается',
    failed: 'ошибка',
    stopped: 'остановлен',
  }[status]
}

onMounted(async () => {
  try {
    session.value = await api<Session>('/api/admin/auth/session')
    if (session.value.authenticated) await loadDashboard()
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : 'Не удалось открыть админку.'
  } finally {
    loading.value = false
  }
})
</script>

<template>
  <main v-if="session && !session.authenticated" class="login-shell">
    <section class="login-card">
      <p class="eyebrow">Контент Завод</p>
      <h1>Вход в админку</h1>
      <p class="muted">
        Постоянного пароля нет. Получи одноразовый код в Telegram или открой ссылку из личной панели.
      </p>
      <button
        v-if="!codeRequested"
        class="primary-button"
        :disabled="actionLoading"
        @click="requestCode"
      >
        {{ actionLoading ? 'Отправляю…' : 'Получить код в Telegram' }}
      </button>
      <form v-else class="code-form" @submit.prevent="login">
        <label for="login-code">Код из Telegram</label>
        <input
          id="login-code"
          v-model="code"
          class="code-input"
          inputmode="numeric"
          autocomplete="one-time-code"
          maxlength="4"
          pattern="[0-9]{4}"
          placeholder="0000"
          required
          autofocus
        >
        <button class="primary-button" :disabled="actionLoading || code.length !== 4">
          {{ actionLoading ? 'Проверяю…' : 'Войти' }}
        </button>
        <button type="button" class="text-button" :disabled="actionLoading" @click="requestCode">
          Отправить новый код
        </button>
      </form>
      <p v-if="error" class="error-banner">{{ error }}</p>
    </section>
  </main>

  <main v-else-if="session?.authenticated" class="admin-shell">
    <header class="topbar">
      <div>
        <p class="eyebrow">Контент Завод</p>
        <h1>Панель производства</h1>
      </div>
      <button class="text-button" @click="logout">Выйти</button>
    </header>

    <section class="bot-strip" aria-label="Состояние ботов">
      <article v-for="bot in bots" :key="bot.slug" class="bot-chip">
        <span class="status-dot" :class="`status-${bot.status}`"></span>
        <div>
          <strong>{{ bot.title }}</strong>
          <small>{{ statusTitle(bot.status) }}<template v-if="bot.username"> · @{{ bot.username }}</template></small>
          <small v-if="bot.lastError" class="bot-error">{{ bot.lastError }}</small>
        </div>
      </article>
    </section>

    <form class="filters" @submit.prevent="loadDashboard">
      <label>
        С
        <input v-model="from" type="date" required>
      </label>
      <label>
        По
        <input v-model="to" type="date" required>
      </label>
      <label>
        Бот
        <select v-model="selectedBot">
          <option value="">Все боты</option>
          <option v-for="slug in allBotSlugs" :key="slug" :value="slug">
            {{ botTitle(slug) }}
          </option>
        </select>
      </label>
      <button class="primary-button compact" :disabled="loading">Обновить</button>
    </form>

    <p v-if="error" class="error-banner">{{ error }}</p>
    <div v-if="loading" class="loading">Собираю показатели…</div>

    <template v-else-if="dashboard">
      <section class="metrics">
        <article class="metric-card accent">
          <span>Выручка</span>
          <strong>{{ rubles(revenueKopecks) }}</strong>
          <small>{{ integer(paymentCount) }} оплаченных операций</small>
        </article>
        <article class="metric-card">
          <span>Себестоимость</span>
          <strong>{{ rubles(costKopecks) }}</strong>
          <small>по событиям расхода</small>
        </article>
        <article class="metric-card">
          <span>Доход до постоянных расходов</span>
          <strong>{{ rubles(revenueKopecks - costKopecks) }}</strong>
          <small>выручка минус ресурсы</small>
        </article>
        <article class="metric-card danger">
          <span>Упавшие задания</span>
          <strong>{{ integer(jobs.length) }}</strong>
          <small>последние 50</small>
        </article>
      </section>

      <section class="panel">
        <div class="panel-heading">
          <div>
            <p class="eyebrow">Воронка</p>
            <h2>От старта до продления</h2>
          </div>
        </div>
        <div class="funnel-grid">
          <article v-for="row in dashboard.funnel" :key="row.botSlug" class="funnel-card">
            <h3>{{ botTitle(row.botSlug) }}</h3>
            <ol>
              <li><span>Старты</span><strong>{{ integer(row.starts) }}</strong></li>
              <li><span>Бесплатный результат</span><strong>{{ integer(row.freeResults) }}</strong></li>
              <li><span>Первая оплата</span><strong>{{ integer(row.payments) }}</strong></li>
              <li><span>Продление</span><strong>{{ integer(row.renewals) }}</strong></li>
            </ol>
          </article>
          <p v-if="dashboard.funnel.length === 0" class="empty">За период событий ещё нет.</p>
        </div>
      </section>

      <section class="two-columns">
        <article class="panel">
          <div class="panel-heading">
            <div>
              <p class="eyebrow">Ресурсы</p>
              <h2>Себестоимость по ботам</h2>
            </div>
          </div>
          <div class="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Бот</th>
                  <th>Операций</th>
                  <th>Расход</th>
                  <th>На пользователя</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in dashboard.usage" :key="row.botSlug">
                  <td>{{ botTitle(row.botSlug) }}</td>
                  <td>{{ integer(row.eventCount) }}</td>
                  <td>{{ rubles(row.costKopecks) }}</td>
                  <td>{{ rubles(row.costPerUserKopecks) }}</td>
                </tr>
                <tr v-if="dashboard.usage.length === 0">
                  <td colspan="4" class="empty">Расходов пока нет.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </article>

        <article class="panel">
          <div class="panel-heading">
            <div>
              <p class="eyebrow">Рефералы</p>
              <h2>Кого привели результаты</h2>
            </div>
          </div>
          <div class="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Метка</th>
                  <th>Пришло</th>
                  <th>Оплатило</th>
                  <th>Выручка</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in dashboard.referrals" :key="`${row.botSlug}:${row.referralCode}`">
                  <td><code>{{ row.referralCode }}</code></td>
                  <td>{{ integer(row.invited) }}</td>
                  <td>{{ integer(row.paidUsers) }}</td>
                  <td>{{ rubles(row.revenueKopecks) }}</td>
                </tr>
                <tr v-if="dashboard.referrals.length === 0">
                  <td colspan="4" class="empty">Переходов по меткам пока нет.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </article>
      </section>

      <section class="panel">
        <div class="panel-heading">
          <div>
            <p class="eyebrow">Контроль</p>
            <h2>Упавшие задания</h2>
          </div>
        </div>
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Когда</th>
                <th>Бот</th>
                <th>Операция</th>
                <th>Попытки</th>
                <th>Ошибка</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="job in jobs" :key="job.id">
                <td>{{ when(job.updatedAt) }}</td>
                <td>{{ botTitle(job.botSlug) }}</td>
                <td><code>{{ job.operationKind }}</code></td>
                <td>{{ job.attempts }}/{{ job.maxAttempts }}</td>
                <td class="error-cell">{{ job.error || 'Без текста ошибки' }}</td>
              </tr>
              <tr v-if="jobs.length === 0">
                <td colspan="5" class="empty">Упавших заданий нет.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="panel">
        <div class="panel-heading">
          <div>
            <p class="eyebrow">Деньги</p>
            <h2>Последние платежи</h2>
          </div>
        </div>
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Когда</th>
                <th>Бот</th>
                <th>Telegram ID</th>
                <th>Сумма</th>
                <th>Статус</th>
                <th>Чек</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="payment in payments" :key="payment.orderId">
                <td>{{ when(payment.createdAt) }}</td>
                <td>{{ botTitle(payment.botSlug) }}</td>
                <td><code>{{ payment.telegramUserId }}</code></td>
                <td>{{ rubles(payment.amountKopecks) }}</td>
                <td><span class="payment-status" :class="`payment-${payment.status}`">{{ payment.status }}</span></td>
                <td>
                  <a v-if="payment.receiptUrl" :href="payment.receiptUrl" target="_blank" rel="noreferrer">Открыть</a>
                  <span v-else>—</span>
                </td>
              </tr>
              <tr v-if="payments.length === 0">
                <td colspan="6" class="empty">Платежей пока нет.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </template>
  </main>

  <main v-else class="login-shell">
    <div class="loading">Открываю админку…</div>
  </main>
</template>
